import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, Shield, FileText, MessageSquare, Download, Clock, Users } from 'lucide-react';
import { mockServices } from '../mock';
import SEO from '../components/SEO';
import { getSEOData, getServiceSchema } from '../utils/seoConfig';

const Services = () => {
  const { language } = useLanguage();

  const content = {
    fr: {
      hero: {
        title: 'Nos services',
        subtitle: 'Des solutions adaptées à chaque situation pour sécuriser vos dépôts de garantie'
      },
      features: {
        title: 'Pourquoi choisir nos services ?',
        items: [
          {
            icon: Shield,
            title: 'Conformité légale garantie',
            description: 'Tous nos modèles sont basés sur le Code civil et le Code du tourisme'
          },
          {
            icon: Download,
            title: 'Accès immédiat',
            description: 'Téléchargez vos documents et commencez à les utiliser dans les minutes qui suivent'
          },
          {
            icon: Clock,
            title: 'Gain de temps',
            description: 'Ne perdez plus des heures à chercher des informations contradictoires'
          },
          {
            icon: Users,
            title: 'Support expert',
            description: 'Notre équipe est là pour répondre à toutes vos questions'
          }
        ]
      },
      services: {
        title: 'Nos offres',
        cta: 'Choisir cette offre'
      },
      comparison: {
        title: 'Comparaison des offres',
        features: [
          'Modèles de documents',
          'Check-lists d\'état des lieux',
          'Guide juridique',
          'Analyse personnalisée',
          'Support email',
          'Accompagnement personnalisé',
          'Suivi de litige'
        ],
        included: 'Inclus',
        notIncluded: 'Non inclus'
      },
      guarantee: {
        title: 'Garantie satisfait ou remboursé',
        description: '30 jours pour essayer nos services sans risque. Si vous n\'êtes pas satisfait, nous vous remboursons intégralement.',
        cta: 'Commencer maintenant'
      }
    },
    en: {
      hero: {
        title: 'Our Services',
        subtitle: 'Solutions adapted to every situation to secure your security deposits'
      },
      features: {
        title: 'Why choose our services?',
        items: [
          {
            icon: Shield,
            title: 'Guaranteed legal compliance',
            description: 'All our templates are based on the Civil Code and Tourism Code'
          },
          {
            icon: Download,
            title: 'Immediate access',
            description: 'Download your documents and start using them within minutes'
          },
          {
            icon: Clock,
            title: 'Time saving',
            description: 'Stop wasting hours searching for contradictory information'
          },
          {
            icon: Users,
            title: 'Expert support',
            description: 'Our team is here to answer all your questions'
          }
        ]
      },
      services: {
        title: 'Our Offers',
        cta: 'Choose this offer'
      },
      comparison: {
        title: 'Offer Comparison',
        features: [
          'Document templates',
          'Property inspection checklists',
          'Legal guide',
          'Personalized analysis',
          'Email support',
          'Personalized support',
          'Dispute tracking'
        ],
        included: 'Included',
        notIncluded: 'Not included'
      },
      guarantee: {
        title: 'Money-back guarantee',
        description: '30 days to try our services risk-free. If you\'re not satisfied, we\'ll refund you in full.',
        cta: 'Start now'
      }
    }
  };

  const t = content[language];

  const comparisonData = [
    { packModeles: true, auditExpress: false, accompagnementLitige: false },
    { packModeles: true, auditExpress: false, accompagnementLitige: false },
    { packModeles: true, auditExpress: true, accompagnementLitige: true },
    { packModeles: false, auditExpress: true, accompagnementLitige: true },
    { packModeles: false, auditExpress: true, accompagnementLitige: true },
    { packModeles: false, auditExpress: false, accompagnementLitige: true },
    { packModeles: false, auditExpress: false, accompagnementLitige: true }
  ];

  const seoData = getSEOData('services', language);
  const servicesSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "item": getServiceSchema("Pack Modèles Propriétaire", "147", "Modèles professionnels pour sécuriser vos locations")
      },
      {
        "@type": "ListItem",
        "position": 2,
        "item": getServiceSchema("Audit Express", "197", "Audit complet de votre état des lieux")
      },
      {
        "@type": "ListItem",
        "position": 3,
        "item": getServiceSchema("Accompagnement Litige", "497", "Support complet en cas de litige")
      }
    ]
  };

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        url="/services"
        schema={servicesSchema}
        language={language}
      />
      <Navbar />
      <div className="min-h-screen">
        {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-50 via-blue-50 to-slate-50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
            {t.hero.title}
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            {t.hero.subtitle}
          </p>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-12 text-center">
            {t.features.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {t.features.items.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div key={index} className="text-center">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="h-8 w-8 text-blue-900" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600 text-sm">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-12 text-center">
            {t.services.title}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {mockServices.map((service) => (
              <Card
                key={service.id}
                className={`relative overflow-hidden shadow-lg hover:shadow-xl transition-all ${
                  service.popular ? 'border-2 border-blue-900 transform md:-translate-y-4' : ''
                }`}
              >
                {service.popular && (
                  <div className="absolute top-0 right-0 bg-blue-900 text-white px-4 py-1 text-sm font-semibold">
                    {language === 'fr' ? 'Le plus populaire' : 'Most Popular'}
                  </div>
                )}
                <CardHeader className="pb-4">
                  <CardTitle className="text-2xl font-bold text-slate-900">
                    {service.title[language]}
                  </CardTitle>
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-blue-900">€{service.price}</span>
                  </div>
                  <p className="text-slate-600 mt-4">{service.description[language]}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-4 mb-8">
                    {service.features[language].map((feature, index) => (
                      <li key={index} className="flex items-start space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    asChild
                    className="w-full bg-blue-900 hover:bg-blue-800 text-white"
                    size="lg"
                  >
                    <Link to="/contact">{t.services.cta}</Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-12 text-center">
            {t.comparison.title}
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="border-b-2 border-slate-300">
                  <th className="text-left py-4 px-4 font-semibold text-slate-900"></th>
                  {mockServices.map((service) => (
                    <th key={service.id} className="text-center py-4 px-4">
                      <div className="font-bold text-slate-900">{service.title[language]}</div>
                      <div className="text-2xl font-bold text-blue-900 mt-2">€{service.price}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.comparison.features.map((feature, index) => (
                  <tr key={index} className="border-b border-slate-200">
                    <td className="py-4 px-4 text-slate-700">{feature}</td>
                    <td className="text-center py-4 px-4">
                      {comparisonData[index].packModeles ? (
                        <CheckCircle className="h-6 w-6 text-green-600 mx-auto" />
                      ) : (
                        <span className="text-slate-400 text-sm">{t.comparison.notIncluded}</span>
                      )}
                    </td>
                    <td className="text-center py-4 px-4">
                      {comparisonData[index].auditExpress ? (
                        <CheckCircle className="h-6 w-6 text-green-600 mx-auto" />
                      ) : (
                        <span className="text-slate-400 text-sm">{t.comparison.notIncluded}</span>
                      )}
                    </td>
                    <td className="text-center py-4 px-4">
                      {comparisonData[index].accompagnementLitige ? (
                        <CheckCircle className="h-6 w-6 text-green-600 mx-auto" />
                      ) : (
                        <span className="text-slate-400 text-sm">{t.comparison.notIncluded}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="py-16 bg-gradient-to-br from-green-50 to-blue-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <Shield className="h-16 w-16 text-green-600 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              {t.guarantee.title}
            </h2>
            <p className="text-lg text-slate-600 mb-8">{t.guarantee.description}</p>
            <Button
              asChild
              size="lg"
              className="bg-blue-900 hover:bg-blue-800 text-white"
            >
              <Link to="/contact">{t.guarantee.cta}</Link>
            </Button>
          </div>
        </div>
      </section>
      </div>
      <Footer />
    </>
  );
};

export default Services;
